import { select, call, takeLatest, put } from 'redux-saga/effects';

import { CreditActions } from '../constants';
import { CreditApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import { DeleteCreditAction, GetCreditAction, GetCreditsAction, PersistCreditAction } from '../actions/credit-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupCredit } from '../../helpers/credit';

function* handleFetchCredits(action: GetCreditsAction) {
    const oldData = yield select((state: StoreState) => state.credits.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        CreditApi.getCredits({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchCredit(action: GetCreditAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => CreditApi.getCredit(id));
}

function* handleDeleteCredit(action: DeleteCreditAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => CreditApi.deleteCredit(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistCredit(action: PersistCreditAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    const groupData = groupCredit(restData);

    yield resolveApiCall(
        action,
        () => (id ? CreditApi.updateCredit(id, groupData) : CreditApi.createCredit(groupData)),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);
            //  if (!id) { }
            yield put(push(Router.generate(routes.CREDITS_LIST)));
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(CreditActions.FETCH_CREDITS, handleFetchCredits);
    yield takeLatest(CreditActions.FETCH_CREDIT, handleFetchCredit);
    yield takeLatest(CreditActions.DELETE_CREDIT, handleDeleteCredit);
    yield takeLatest(CreditActions.PERSIST_CREDIT, handlePersistCredit);
}
